/**
 * API client.
 *
 * A thin typed wrapper around fetch. All API calls go through here.
 *
 * Responsibilities:
 * - Prepend the API base URL
 * - Inject Authorization header from stored access token
 * - Handle 401 → attempt token refresh → retry once
 * - Normalise errors into typed ApiErrorResponse
 *
 * Why not axios?
 * - Fetch is built into modern browsers and Node 18+.
 * - Keeps the bundle smaller.
 * - The fetch API is now stable and feature-complete for our needs.
 */

import { ApiResponse } from '@accestra/types';
import i18n from '../i18n/i18n';
import { LANGUAGE_STORAGE_KEY, normalizeLocale, resolvePreferredLocale } from '../i18n/localeTypes';

const API_BASE = import.meta.env['VITE_API_BASE_URL'] ?? '';

// -------------------------------------------------------------------
// Token storage
// Access + refresh tokens are persisted to localStorage so sessions
// survive page refreshes. On app boot, AuthProvider calls
// restoreTokensFromStorage() to repopulate these vars before any
// auth-gated render decisions are made.
//
// Security note: localStorage tokens are readable by JS (XSS risk).
// A hardening step would move refresh tokens to httpOnly cookies.
// That requires backend changes and is deferred to a future phase.
// -------------------------------------------------------------------

const LS_ACCESS  = 'accestra_at';
const LS_REFRESH = 'accestra_rt';
const LS_ORG_ID  = 'accestra_org';
const LS_ROLE    = 'accestra_role';

let _accessToken: string | null = null;
let _refreshToken: string | null = null;
let _orgId: string | null = null;
let _role: string | null = null;

function hydrateTokensFromStorage(): boolean {
  if (_accessToken && _refreshToken && _orgId && _role) {
    return true;
  }

  return restoreTokensFromStorage();
}

function resolvePreferredLocaleHeader(): string {
  let storedLocale: string | null = null;
  try {
    storedLocale = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }

  if (typeof navigator !== 'undefined') {
    return resolvePreferredLocale({
      savedLocale: storedLocale,
      browserLanguages: Array.isArray(navigator.languages)
        ? [navigator.language, ...navigator.languages]
        : [navigator.language],
      fallbackLocale: 'en',
    });
  }

  return normalizeLocale(storedLocale);
}

export function setTokens(
  accessToken: string,
  refreshToken: string,
  orgId: string,
  role: string,
): void {
  _accessToken  = accessToken;
  _refreshToken = refreshToken;
  _orgId        = orgId;
  _role         = role;
  try {
    localStorage.setItem(LS_ACCESS,  accessToken);
    localStorage.setItem(LS_REFRESH, refreshToken);
    localStorage.setItem(LS_ORG_ID,  orgId);
    localStorage.setItem(LS_ROLE,    role);
  } catch { /* private/incognito mode — ignore */ }
}

export function clearTokens(): void {
  _accessToken  = null;
  _refreshToken = null;
  _orgId        = null;
  _role         = null;
  try {
    localStorage.removeItem(LS_ACCESS);
    localStorage.removeItem(LS_REFRESH);
    localStorage.removeItem(LS_ORG_ID);
    localStorage.removeItem(LS_ROLE);
  } catch { /* ignore */ }
}

/**
 * Restore in-memory token vars from localStorage.
 * Returns true when a stored access token was found.
 * Call this once on app boot before any auth-gated renders.
 */
export function restoreTokensFromStorage(): boolean {
  try {
    const at   = localStorage.getItem(LS_ACCESS);
    const rt   = localStorage.getItem(LS_REFRESH);
    const org  = localStorage.getItem(LS_ORG_ID);
    const role = localStorage.getItem(LS_ROLE);
    if (at && rt && org && role) {
      _accessToken  = at;
      _refreshToken = rt;
      _orgId        = org;
      _role         = role;
      return true;
    }
  } catch { /* private/incognito mode — ignore */ }
  return false;
}

export function hasAccessToken(): boolean {
  hydrateTokensFromStorage();
  return _accessToken !== null;
}

// -------------------------------------------------------------------
// Core request function
// -------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly statusCode: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipAuth = false, ...init } = options;

  if (!skipAuth) {
    hydrateTokensFromStorage();
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': resolvePreferredLocaleHeader(),
    ...(init.headers as Record<string, string>),
  };

  if (!skipAuth && _accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }

  const response = await fetch(`${API_BASE}/api/v1${path}`, {
    ...init,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Handle 401 → try refresh → retry
  if (response.status === 401 && !skipAuth && _refreshToken) {
    const refreshed = await tryRefreshTokens();
    if (refreshed) {
      // Retry original request with new token
      headers['Authorization'] = `Bearer ${_accessToken}`;
      const retryResponse = await fetch(`${API_BASE}/api/v1${path}`, {
        ...init,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      return parseResponse<T>(retryResponse);
    } else {
      clearTokens();
      throw new ApiError(
        'UNAUTHORIZED',
        i18n.t('common:errors.unauthorized', {
          defaultValue: 'Session expired. Please log in again.',
        }),
        401,
      );
    }
  }

  return parseResponse<T>(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
  // 429: capture the Retry-After header BEFORE consuming the body.
  // The header is only available on the Response object here; once we
  // hand the body JSON to the caller the header is gone.
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After');
    const retrySeconds = retryAfter !== null ? parseInt(retryAfter, 10) : NaN;
    const waitMsg = !isNaN(retrySeconds)
      ? i18n.t('common:errors.rateLimitedWithRetry', {
          seconds: retrySeconds,
          defaultValue: `Too many requests. Please wait ${retrySeconds} seconds before trying again.`,
        })
      : i18n.t('common:errors.rateLimited', {
          defaultValue: 'Too many requests. Please wait before trying again.',
        });
    throw new ApiError('RATE_LIMITED', waitMsg, 429, {
      retryAfterSeconds: !isNaN(retrySeconds) ? retrySeconds : null,
    });
  }

  const data: ApiResponse<T> = await response.json();

  if (!data.success) {
    throw new ApiError(
      data.error.code,
      data.error.message,
      response.status,
      data.error.details,
    );
  }

  return (data as { success: true; data: T }).data;
}

/**
 * Convert an unknown thrown value into a user-facing message, using the HTTP
 * status code to provide clear, actionable framing for common API errors.
 *
 * - 403 → "You do not have access to this resource."
 * - 404 → "This resource could not be found."
 * - 429 → message already built with Retry-After info above
 * - everything else → the server-supplied message (or `fallback`)
 *
 * Importantly, this function does NOT clear tokens or trigger a logout —
 * call sites should only call clearTokens() / navigate('/login') on 401.
 */
export function friendlyError(err: unknown, fallback = 'Something went wrong.'): string {
  if (!(err instanceof ApiError)) return fallback;
  const code = err.code.toLowerCase();
  switch (err.statusCode) {
    case 400:
      if (
        code === 'validation_failed'
        || code === 'request_validation_failed'
        || err.message.toLowerCase().includes('validation')
      ) {
        return i18n.t('common:errors.validation', {
          defaultValue: 'Request validation failed.',
        });
      }
      return err.message || fallback;
    case 401:
      return i18n.t('common:errors.unauthorized', {
        defaultValue: 'Session expired. Please log in again.',
      });
    case 403:
      return err.message || i18n.t('common:errors.forbidden', {
        defaultValue: 'You do not have access to this resource.',
      });
    case 404:
      return i18n.t('common:errors.notFound', {
        defaultValue: 'This resource could not be found.',
      });
    case 409:
      return i18n.t('common:errors.conflict', {
        defaultValue: 'This action could not be completed because the resource changed.',
      });
    // 429 message is already friendly (built with Retry-After in parseResponse)
    default:
      return err.message || fallback;
  }
}

async function tryRefreshTokens(): Promise<boolean> {
  hydrateTokensFromStorage();
  if (!_refreshToken || !_orgId || !_role) return false;

  try {
    const response = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': resolvePreferredLocaleHeader(),
      },
      body: JSON.stringify({ refreshToken: _refreshToken, orgId: _orgId, role: _role }),
    });

    if (!response.ok) return false;

    const data = await response.json();
    if (data.success && data.data.tokens) {
      setTokens(data.data.tokens.accessToken, data.data.tokens.refreshToken, _orgId, _role);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

// -------------------------------------------------------------------
// Public API methods
// -------------------------------------------------------------------

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/**
 * Make an authenticated raw fetch (no JSON parsing).
 * Use this when you need the raw Response, e.g. to download a file as a Blob.
 * Injects the stored Bearer token exactly like the JSON `api.*` helpers do.
 */
export async function fetchAuthenticated(path: string): Promise<Response> {
  const headers: Record<string, string> = {
    'Accept-Language': resolvePreferredLocaleHeader(),
  };
  if (_accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  return fetch(`${API_BASE}/api/v1${path}`, { headers });
}
